import type { PropsWithChildren } from "react";

import type { CheckoutAttemptStatus } from "@/react/types/PaywallCheckoutAttempt";
import type { PaywallPriceType } from "@/react/types/PaywallPrice";

import type { CheckoutError } from "../CheckoutError";

/**
 * Checkout mode determines the behavior of the PaywallCheckoutAttemptsProvider.
 *
 * - `"default"`: Standard checkout flow. Shows payment and address forms as needed.
 *   User can enter a new payment method or use a saved one.
 *
 * - `"express"`: Express checkout mode for quick payments using saved data.
 *   Designed for upsells and one-click purchases. Uses saved payment method only,
 *   skips address collection. Renders PaymentElement hidden to detect saved method
 *   availability. Raises error if no saved payment method exists.
 */
export const paywallCheckoutAttemptsModes = {
  default: "default",
  express: "express",
} as const;
export type PaywallCheckoutAttemptsMode =
  (typeof paywallCheckoutAttemptsModes)[keyof typeof paywallCheckoutAttemptsModes];

export interface CommunityProcessorProps {
  communityProcessorId: string;
  communityProcessorKey: string;
  communityProcessorPaymentMethodConfigurationId: string;
  doesCommunityProcessorSupportsLink: boolean;
}

export interface CommunityProps extends CommunityProcessorProps {
  communityId: number;
}
export interface MemberAddressProps {
  isMemberAddressComplete: boolean;
  memberAddressCity?: string;
  memberAddressCountry?: string;
  memberAddressLine1?: string;
  memberAddressLine2?: string;
  memberAddressPostalCode?: string;
  memberAddressState?: string;
}

export interface MemberProps extends MemberAddressProps {
  doesMemberHasDefaultPaymentMethod: boolean;
  memberEmail?: string;
  memberId?: number;
  memberName?: string;
  memberProcessorCustomerSessionClientSecret?: string;
  memberUserId?: number;
}

export interface OrderProps {
  checkoutIntentToken?: string;
  orderAmount: number;
  orderCurrency: string;
  orderCurrencyMinimumChargeAmount: number;
  orderPriceType: PaywallPriceType;
  paywallId: number;
  paywallPriceId: number;
  sessionId?: number;
  sessionStepId?: number;
}

export const stages = {
  idle: "idle",
  settingUpPaymentMethod: "settingUpPaymentMethod",
  creatingAttempt: "creatingAttempt",
  confirmingAttempt: "confirmingAttempt",
  verifyingAttempt: "verifyingAttempt",
  completed: "completed",
  failed: "failed",
} as const;
export type Stage = (typeof stages)[keyof typeof stages];

interface OnChangeStageToCompleteProps {
  attemptId: number;
  attemptStatus: CheckoutAttemptStatus;
  invoiceId?: string;
  paymentIntentId?: string;
  paymentMethodType: string;
  stage: "completed";
  subscriptionId?: string;
  targetTrialDays?: number;
  userSignedIn?: boolean;
}
interface OnChangeStageToErrorProps {
  error: CheckoutError;
  stage: "failed";
}
interface OnChangeStageToProgressProps {
  stage:
    | "settingUpPaymentMethod"
    | "creatingAttempt"
    | "confirmingAttempt"
    | "verifyingAttempt";
}
export type OnStageChangeProps =
  | OnChangeStageToCompleteProps
  | OnChangeStageToErrorProps
  | OnChangeStageToProgressProps;
export type OnStageChange = (props: OnStageChangeProps) => void;

// TODO: remove these props once consumers resolve the confirmation URL from the BE
// - invoiceId
// - paymentIntentId
// - subscriptionId
export interface OnCompleteProps {
  attemptId: number;
  attemptStatus: CheckoutAttemptStatus;
  invoiceId?: string;
  paymentIntentId?: string;
  paymentMethodType: string;
  subscriptionId?: string;
  userSignedIn?: boolean;
}
export type OnComplete = (props: OnCompleteProps) => Promise<void> | void;

export interface OnErrorProps {
  error: CheckoutError;
}
export type OnError = (props: OnErrorProps) => void;
export type OnCheckoutTermsChanged = () => Promise<void>;

export type OnProgress = (stage: Stage) => void;

export interface BasePaywallsCheckoutAttemptsContextProps
  extends CommunityProps, MemberProps, OrderProps {}
export interface BasePaywallCheckoutAttemptsContextProviderProps
  extends PropsWithChildren, BasePaywallsCheckoutAttemptsContextProps {}

export interface MemberAddressState {
  city?: string;
  country?: string;
  email?: string;
  isComplete: boolean;
  line1?: string;
  line2?: string;
  name?: string;
  postalCode?: string;
  state?: string;
}

export interface OnAddressChangeProps {
  city?: string;
  country?: string;
  email?: string;
  isComplete: boolean;
  line1?: string;
  line2?: string;
  name?: string;
  postalCode?: string;
  state?: string;
}
export type OnAddressChange = (props: OnAddressChangeProps) => void;

export interface OnPaymentMethodChangeProps {
  isComplete: boolean;
  isDefaultPaymentMethod: boolean;
  paymentMethodType: string;
}
export type OnPaymentMethodChange = (props: OnPaymentMethodChangeProps) => void;
